import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { initScorm12Api } from '../scorm/scorm12Api'
import { API_BASE_URL } from '../data'
import { useAuth } from '../components'

export default function TrainingScormPlayer() {
    const { trainingId, moduleIndex } = useParams()
    const { user } = useAuth()
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const [scormUrl, setScormUrl] = useState<string>('')
    const [isApiReady, setIsApiReady] = useState(false)
    const [error, setError] = useState<string>('')

    useEffect(() => {
        if (!trainingId || !moduleIndex || !user?.volunteer_csid) return

        let cancelled = false

        async function boot() {
            try {
                const res = await fetch(`${API_BASE_URL}/training/${trainingId}`, {
                    credentials: 'include'
                })
                if (!res.ok) throw new Error('Failed to fetch training')

                const training = await res.json()
                const content =
                    typeof training.content === 'string'
                        ? JSON.parse(training.content)
                        : training.content

                const module = content.modules[Number(moduleIndex)]
                if (!module?.scorm?.launch_url) {
                    throw new Error('SCORM module not configured properly')
                }

                const progressRes = await fetch(
                    `${API_BASE_URL}/training/${trainingId}/volunteer/${user.volunteer_csid}/module/${moduleIndex}/scorm`,
                    { credentials: 'include' }
                )
                const savedProgress = progressRes.ok ? await progressRes.json() : null

                console.log('[SCORM 1.2] Saved progress:', savedProgress)

                // Initialize API FIRST
                initScorm12Api({
                    initialState: savedProgress
                        ? {
                            lesson_status: savedProgress.lesson_status || 'not attempted',
                            lesson_location: savedProgress.lesson_location || '',
                            total_time: savedProgress.total_time || 0,
                            data: savedProgress.data || {}
                        }
                        : undefined,

                    onCommit: async (state) => {
                        console.log('[SCORM 1.2] 📤 onCommit - sending to backend:', state)

                        try {
                            const response = await fetch(
                                `${API_BASE_URL}/training/${trainingId}/volunteer/${user.volunteer_csid}/module/${moduleIndex}/scorm/progress`,
                                {
                                    method: 'PUT',
                                    credentials: 'include',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        lesson_status: state.lesson_status,
                                        lesson_location: state.lesson_location,
                                        total_time: Math.floor(state.total_time),
                                        data: state.data
                                    })

                                }
                            )

                            const result = await response.json()
                            if (result.success) {
                                console.log('[SCORM 1.2] Progress saved successfully')
                            } else {
                                console.error('[SCORM 1.2] Save failed:', result.message)
                            }
                        } catch (error) {
                            console.error('[SCORM 1.2] Network error saving progress:', error)
                        }
                    },

                    onComplete: async () => {
                        console.log('[SCORM 1.2] onComplete - marking module as complete')

                        try {
                            const response = await fetch(
                                `${API_BASE_URL}/training/${trainingId}/volunteer/${user.volunteer_csid}/module/${moduleIndex}/scorm/complete`,
                                {
                                    method: 'PUT',
                                    credentials: 'include'
                                }
                            )

                            const result = await response.json()
                            if (result.success) {
                                console.log('[SCORM 1.2] Module marked as complete')
                            } else {
                                console.error('[SCORM 1.2] Complete failed:', result)
                            }
                        } catch (error) {
                            console.error('[SCORM 1.2] Network error completing module:', error)
                        }
                    }
                })

                if (!cancelled) {
                    let url = module.scorm.launch_url

                    // Simple localhost proxy
                    // try {
                    //     const parsedUrl = new URL(url)
                    //     url = `/scorm-content${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
                    // } catch (e) {
                    //     console.error('[SCORM 1.2] Invalid URL:', url, e)
                    //     if (!url.startsWith('/')) {
                    //         url = `/scorm-content/${url}`
                    //     }
                    // }

                    console.log('[SCORM 1.2] Prepared URL:', url)
                    console.log('[SCORM 1.2] Original launch URL:', module.scorm.launch_url)

                    // Load immediately (non-blocking verification)
                    setScormUrl(url)
                    setIsApiReady(true)

                    // Verify in background
                    // fetch(url, { method: 'HEAD' })
                    //     .then(testRes => {
                    //         if (testRes.ok) {
                    //             console.log('[SCORM 1.2] Content verified accessible')
                    //         } else {
                    //             console.warn('[SCORM 1.2] Verification returned:', testRes.status)
                    //         }
                    //     })
                    //     .catch(err => {
                    //         console.warn('[SCORM 1.2] Verification failed:', err.message)
                    //     })
                }
            } catch (error: any) {
                console.error('[SCORM 1.2] Boot error:', error)
                setError(error.message || 'Failed to load SCORM content')
            }
        }

        boot()

        return () => {
            cancelled = true
            // Cleanup SCORM API
            if ((window as any).__scormCleanup) {
                (window as any).__scormCleanup()
            }
        }
    }, [trainingId, moduleIndex, user])

    if (error) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center max-w-2xl p-8 bg-red-50 border border-red-200 rounded">
                    <h2 className="text-xl font-bold text-red-800 mb-4">SCORM Load Error</h2>
                    <p className="text-red-700 mb-4">{error}</p>
                    <details className="text-start text-sm text-red-600">
                        <summary className="cursor-pointer font-semibold mb-2">Troubleshooting Steps</summary>
                        <ul className="list-disc list-inside space-y-1">
                            <li>Verify SCORM server is running on localhost:8080</li>
                            <li>Check the SCORM package structure and entry point file</li>
                            <li>Ensure launch_url in training data is correct</li>
                            <li>Test URL directly: <code className="bg-red-100 px-1 rounded">http://localhost:8080/index_lms.html</code></li>
                        </ul>
                    </details>
                </div>
            </div>
        )
    }

    if (!isApiReady || !scormUrl) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                    <p>Loading SCORM content...</p>
                </div>
            </div>
        )
    }

    return (
        <iframe
            ref={iframeRef}
            src={scormUrl}
            className="w-full border-0 flex-1 min-h-[600px]"
            title="SCORM Player"
            allow="fullscreen"
        />
    )
}
